/**
 * <site-search>
 *
 * The header's search layer: a panel that drops from the top of the viewport
 * with the search form, optional recommendations and live results.
 *
 * Opening: any element with `data-search-open` (anywhere in the document), or
 *   document.querySelector('site-search').open(openerElement)
 * Closing: elements with `data-search-close` inside it, Escape, or .close().
 *
 * Attributes:
 *   data-live="true"   fetch results while typing (settings.enable_quick_header_search)
 *   data-meta="true"   also match tags and SKUs (settings.quick_search_search_meta)
 *
 * Results are rendered by sections/predictive-search.liquid. Where the
 * predictive search endpoint is unavailable for the storefront language the
 * same section is requested through the regular search route instead.
 *
 * Events (bubbling, on the element): on:search:open, on:search:close.
 */
(() => {
  if (customElements.get('site-search')) return;

  const FIELDS = 'title,product_type,variants.title,vendor';
  const META_FIELDS = `${FIELDS},tag,variants.sku`;
  const DELAY = 320;
  const LIMIT = 8;

  const predictiveSupported = () => {
    try {
      const features = JSON.parse(document.getElementById('shopify-features').textContent);
      return features.predictiveSearch !== false;
    } catch (error) {
      return true;
    }
  };

  class SiteSearch extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('[data-search-panel]') || this;
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[name="q"]');
      this.results = this.querySelector('[data-search-results]');
      this.status = this.querySelector('[data-search-status]');
      if (!this.form || !this.input) return;

      this.terms = '';
      this.live = this.dataset.live === 'true' && Boolean(this.results);
      this.usePredictive = predictiveSupported() && Boolean(theme.routes && theme.routes.predictiveSearch);

      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onInput = this.onInput.bind(this);

      document.addEventListener('click', this.onDocumentClick);
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
      if (this.live) {
        this.input.addEventListener('input', this.onInput);
        this.input.addEventListener('search', this.onInput);
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
      if (this.input) {
        this.input.removeEventListener('input', this.onInput);
        this.input.removeEventListener('search', this.onInput);
      }
      this.cancel();
      clearTimeout(this.hideTimer);
      if (this.isOpen) {
        this.isOpen = false;
        theme.releaseFocus();
        theme.unlockScroll();
      }
      if (this.shell) this.shell.classList.remove('is-raised');
    }

    /* -- Open / close ----------------------------------------------------- */

    onDocumentClick(event) {
      const opener = event.target.closest('[data-search-open]');
      if (!opener || event.defaultPrevented) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      this.open(opener);
    }

    onClick(event) {
      if (event.target.closest('[data-search-close]')) {
        event.preventDefault();
        this.close();
      }
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.isOpen) {
        event.stopPropagation();
        this.close();
      }
    }

    open(opener) {
      if (this.isOpen || !this.input) return;
      this.isOpen = true;
      this.opener = opener || document.activeElement;
      clearTimeout(this.hideTimer);

      this.shell = this.closest('.shopify-section');
      if (this.shell) this.shell.classList.add('is-raised');
      this.hidden = false;
      // Commit the hidden -> visible change before the transition class lands.
      void this.offsetWidth;
      this.classList.add('is-open');
      document.querySelectorAll('[data-search-open]').forEach((element) => element.setAttribute('aria-expanded', 'true'));

      theme.lockScroll();
      theme.trapFocus(this.panel, this.input);
      theme.emit('on:search:open', {}, this);
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.classList.remove('is-open');
      document.querySelectorAll('[data-search-open]').forEach((element) => element.setAttribute('aria-expanded', 'false'));

      theme.releaseFocus(this.opener && this.opener.isConnected ? this.opener : null);
      theme.unlockScroll();

      const duration = parseFloat(getComputedStyle(this.panel).transitionDuration) * 1000 || 0;
      this.hideTimer = setTimeout(() => {
        if (this.isOpen) return;
        this.hidden = true;
        if (this.shell) this.shell.classList.remove('is-raised');
      }, duration + 30);
      theme.emit('on:search:close', {}, this);
    }

    /* -- Live results ----------------------------------------------------- */

    cancel() {
      clearTimeout(this.timer);
      if (this.controller) this.controller.abort();
      this.controller = null;
    }

    onInput() {
      const terms = this.input.value.trim();
      if (terms === this.terms) return;
      this.terms = terms;
      this.cancel();

      if (!terms) {
        this.render(null);
        return;
      }
      this.classList.add('is-loading');
      this.timer = setTimeout(() => this.load(terms), DELAY);
    }

    requestUrl(terms) {
      let url;
      if (this.usePredictive) {
        url = new URL(theme.routes.predictiveSearch, window.location.origin);
        url.searchParams.set('resources[type]', 'product,collection,page,article');
        url.searchParams.set('resources[limit]', LIMIT);
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('resources[options][fields]', this.dataset.meta === 'true' ? META_FIELDS : FIELDS);
      } else {
        url = new URL(this.form.getAttribute('action'), window.location.origin);
        url.searchParams.set('type', 'product,page,article');
        url.searchParams.set('options[prefix]', 'last');
      }
      url.searchParams.set('q', terms);
      url.searchParams.set('section_id', 'predictive-search');
      return url.toString();
    }

    async load(terms) {
      const controller = new AbortController();
      this.controller = controller;

      try {
        const response = await fetch(this.requestUrl(terms), { signal: controller.signal });
        if (!response.ok) {
          if (this.usePredictive) {
            // Endpoint refused (unsupported language, throttling): use the search route from now on.
            this.usePredictive = false;
            if (terms === this.terms) this.load(terms);
            return;
          }
          throw new Error(`Search request failed: ${response.status}`);
        }
        const markup = await response.text();
        if (controller.signal.aborted || terms !== this.terms) return;
        this.render(new DOMParser().parseFromString(markup, 'text/html').querySelector('[data-predictive-results]'));
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.render(null);
      } finally {
        if (this.controller === controller) this.controller = null;
      }
    }

    /** @param {Element|null} payload - [data-predictive-results] from the section response. */
    render(payload) {
      this.classList.remove('is-loading');
      this.classList.toggle('has-results', Boolean(payload));
      this.results.replaceChildren(...(payload ? Array.from(payload.childNodes) : []));
      if (this.status) this.status.textContent = payload ? payload.getAttribute('data-status') || '' : '';
      if (payload) this.panel.scrollTop = 0;
    }
  }

  customElements.define('site-search', SiteSearch);
})();
